import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';
import { globalFirebaseConfig } from '../utils/constants';

// Cache em memória para a sessão atual (evita chamadas duplicadas)
const sessionCache = new Map();

// Cache persistido no localStorage (sobrevive reloads, TTL de 24h)
const LS_KEY = 'rplay_img_cache_v2';
const TTL_MS = 24 * 60 * 60 * 1000;

const readLocalCache = () => {
    try {
        const raw = localStorage.getItem(LS_KEY);
        return raw ? JSON.parse(raw) : {};
    } catch {
        return {};
    }
};

const writeLocalCache = (path, url) => {
    try {
        const cache = readLocalCache();
        cache[path] = { url, expiresAt: Date.now() + TTL_MS };
        localStorage.setItem(LS_KEY, JSON.stringify(cache));
    } catch {
        // localStorage cheio ou desabilitado — ignora
    }
};

/**
 * Retorna URL do localStorage de forma SÍNCRONA.
 * Usada para inicializar o state do hook sem piscar.
 */
export const getCachedSync = (path) => {
    if (!path || path.startsWith('http')) return path || null;
    if (sessionCache.has(path)) return sessionCache.get(path);
    try {
        const cache = readLocalCache();
        const entry = cache[path];
        if (entry && Date.now() <= entry.expiresAt) {
            sessionCache.set(path, entry.url);
            return entry.url;
        }
    } catch { /* noop */ }
    return null;
};

const getStorageBucket = () =>
    globalFirebaseConfig?.storageBucket || 'ranking-rp-metodos-4aad2.firebasestorage.app';

/**
 * Constrói URL direta do Firebase Storage.
 * Funciona para arquivos com leitura pública nas Storage Rules (allow read).
 */
const buildDirectUrl = (path) => {
    const bucket = getStorageBucket();
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(cleanPath)}?alt=media`;
};

export const ImageService = {
    /**
     * Resolve a URL de uma imagem do Storage.
     * Ordem: sessionCache → localStorage → URL direta (sem HEAD) → getDownloadURL
     */
    getUrl: async (path) => {
        if (!path) return null;
        if (path.startsWith('http')) return path;

        // 1. Cache de sessão (sem I/O, instantâneo)
        if (sessionCache.has(path)) return sessionCache.get(path);

        // 2. Cache do localStorage (sem rede)
        const cached = getCachedSync(path);
        if (cached) return cached;

        // 3. URL direta — sem HEAD, sem round-trip extra.
        //    O Storage tem "allow read" público, então a URL funciona direto.
        const directUrl = buildDirectUrl(path);
        sessionCache.set(path, directUrl);
        writeLocalCache(path, directUrl);
        return directUrl;
    },

    /**
     * Tenta getDownloadURL como fallback quando a URL direta retorna 403/404
     * (arquivo privado ou que exige token). Chamado pelo onError do <img>.
     */
    getFallbackUrl: async (path) => {
        if (!path || path.startsWith('http')) return null;
        try {
            const url = await getDownloadURL(ref(storage, path));
            sessionCache.set(path, url);
            writeLocalCache(path, url);
            return url;
        } catch {
            console.warn(`[ImageService] Sem acesso ao arquivo: ${path}`);
            return null;
        }
    },

    /**
     * Pré-carrega uma lista de imagens em paralelo no boot do app.
     * Resolve as URLs E dispara requisições HTTP reais para popular o cache do browser.
     * Na próxima vez que o usuário abrir a lojinha, as imagens já estão no cache HTTP.
     */
    preloadImages: async (paths) => {
        const results = await Promise.allSettled(paths.map(p => ImageService.getUrl(p)));
        // Dispara o download real de cada imagem sem bloquear a UI
        results.forEach(r => {
            if (r.status === 'fulfilled' && r.value) {
                const img = new Image();
                img.src = r.value;
                // Sem onload/onerror — fire-and-forget, o browser cuida do cache HTTP
            }
        });
        return results;
    },

    /**
     * Invalida o cache completo (use quando imagens forem trocadas no Storage).
     */
    clearCache: () => {
        sessionCache.clear();
        try { localStorage.removeItem(LS_KEY); } catch { /* noop */ }
    },
};
